"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";

import { loginWithPiAndBackend } from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import usePiBrowser from "@/features/landing/hooks/usePiBrowser";

export default function LoginButton({ compact = false }) {
  const router = useRouter();
  const { login, getRedirectPath } = useAuth();
  const [loading, setLoading] = useState(false);
  const isPiBrowser = usePiBrowser();

  const handleLogin = async () => {
    if (typeof window === "undefined") return;

    if (isPiBrowser === null) return toast.info("Detecting Pi Browser…");

    if (isPiBrowser === false) return toast.error("📱 Open in Pi Browser.");

    if (window.__piReady) {
      const ready = await window.__piReady;
      if (!ready) {
        toast.error("Pi SDK not ready yet. Please try again in a moment.");
        return;
      }
    }

    if (!window.Pi) {
      toast.error("Pi SDK not available. Please reload in Pi Browser.");
      return;
    }

    setLoading(true);

    try {
      const { user, access, piToken } = await loginWithPiAndBackend();

      login(access, piToken);

      toast.success(`👋 Welcome, ${user?.username || "Pi User"}!`);

      const redirect = getRedirectPath?.() || "/app";
      router.push(redirect);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Login failed.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleLogin}
      disabled={loading || isPiBrowser === null}
      className={`${
        compact
          ? "text-sm px-3 py-1 border border-border rounded-md bg-primary text-white hover:bg-accent"
          : "px-4 py-2 rounded-md bg-primary text-white hover:bg-accent"
      } transition duration-150 disabled:opacity-50`}
    >
      {loading ? "Logging in..." : isPiBrowser === null ? "Checking..." : "Login with Pi"}
    </button>
  );
}